import { Dispatch } from "redux";
import {
    submitVote,
    submitAnonymousvote,
    updateAnonymousVote,
    postDeletePoll,
    createComment,
    postdeleteComment,
    updateComment,
    getSinglePoll,
    updateClosePoll,
    putLikePoll,
    deleteAnonymousVote,
    replaceAnonymousVote,
} from "@Services/API/Polls";
import {
    IPoll,
    IPollResponse,
    SubmitVoteRequest,
    UpdateVoteRequest,
} from "@Interfaces";
import { actions as ChannelReducer } from "../channels";
import { AuthSelectors, AppThunk, NotificationActions } from "@Store";
import { actions as PollReducer } from "./slice";
import { ReportService } from "@Services";
import ChannelActions from "../channels/actions";

export type SubmitVoteOutcome = boolean | "already_registered";

// Prevent duplicate report requests per poll while a request is in flight.
// Store the in-flight promise so subsequent calls can return the same one.
const inFlightReportRequests = new Map<string, Promise<void>>();

const buildChannelPoll = (
    data: IPollResponse,
    voteId: string,
    channelId?: string | null
): IPoll => ({
    ...data,
    vote_id: voteId,
    ...(channelId ? { channel_id: channelId } : {}),
});

const {
    updatePollInChannel,
    removePoll,
    removeComment,
    updateCommentsInPoll,
    toggleLikePollInPoll,
    closePoll,
} = ChannelReducer;

const { failedVotingAttempt } = PollReducer;

const PolllActions = {
    updateComment:
        (
            channelId: string,
            pollId: string,
            commentId: string,
            comment: { body: string }
        ) =>
        async (dispatch: Dispatch) => {
            try {
                let updateCommentResponse = await updateComment(
                    pollId,
                    commentId,
                    comment
                );
                dispatch(
                    updateCommentsInPoll({
                        pollId: pollId,
                        channelId: channelId,
                        commentId: commentId,
                        updatedComment: updateCommentResponse.data.body,
                    })
                );
            } catch (error) {
                console.log(error);
            }
        },

    addCommentInPoll:
        (pollId: string, payload: { body: string; username?: string }) =>
        async (dispatch: Dispatch) => {
            try {
                await createComment(pollId, payload);
            } catch (error) {
                console.error(error);
            }
        },

    submitVote:
        (
            pollId: string,
            channelId: string | null | undefined,
            choice: string,
            body: SubmitVoteRequest
        ): AppThunk<Promise<SubmitVoteOutcome>> =>
        async (dispatch) => {
            try {
                const { gender, id: userId } =
                    await AuthSelectors.getUserData();
                const guestStoredData = localStorage.getItem("guest-data");
                let anonymousVotePayload = { ...body };

                if (guestStoredData) {
                    const parsedData = JSON.parse(guestStoredData);
                    anonymousVotePayload = { ...body, ...parsedData };
                }

                // if no user_id, we say is an anonymous vote
                const votingRequest = () =>
                    !userId
                        ? submitAnonymousvote(pollId, anonymousVotePayload)
                        : submitVote(pollId, {
                              ...body,
                              demographic: {
                                  date_range: "",
                                  gender,
                              },
                          });

                const { data, vote_id } = await votingRequest();
                const pollPayload = buildChannelPoll(data, vote_id, channelId);
                if (channelId) {
                    // Ensure channel_id is present; NPS responses can omit it.
                    dispatch(updatePollInChannel({ poll: pollPayload }));
                } else {
                    dispatch(PollReducer.updatePollSliceVotes(pollPayload));
                }
                return true;
            } catch (error) {
                console.error(error);
                const message =
                    // @ts-ignore
                    error?.response?.data?.message ||
                    // @ts-ignore
                    error?.message;

                if (message === "channel_inactive") {
                    dispatch(
                        NotificationActions.notification({
                            snackbarOpen: true,
                            snackbarType: "error",
                            snackbarMessage:
                                "This event is unpublished right now.",
                        })
                    );
                }
                dispatch(failedVotingAttempt()); //notify user that voted did not work
                if (
                    message === "vote_already_registered" ||
                    message === "users_vote_already_registered"
                ) {
                    return "already_registered";
                }
                return false;
            }
        },
    updateVote:
        (pollId: string, body: UpdateVoteRequest) =>
        async (dispatch: Dispatch) => {
            try {
                const requestData = {
                    ...body,
                };

                await updateAnonymousVote(pollId, requestData);
            } catch (error) {
                console.error(error);
                dispatch(failedVotingAttempt()); //notify user that voted did not work
            }
        },
    deleteGuestVote:
        (pollId: string, channelId: string | null | undefined) =>
        async (dispatch: Dispatch) => {
            try {
                const { data, vote_id } = await deleteAnonymousVote(pollId);
                const pollPayload = buildChannelPoll(data, vote_id, channelId);

                if (channelId) {
                    dispatch(updatePollInChannel({ poll: pollPayload }));
                } else {
                    dispatch(PollReducer.updatePollSliceVotes(pollPayload));
                }
            } catch (error) {
                console.error(error);
                dispatch(failedVotingAttempt());
            }
        },
    replaceGuestVote:
        (
            pollId: string,
            channelId: string | null | undefined,
            choiceId: string
        ) =>
        async (dispatch: Dispatch) => {
            try {
                const { data, vote_id } = await replaceAnonymousVote(pollId, {
                    choice_id: choiceId,
                });
                const pollPayload = buildChannelPoll(data, vote_id, channelId);

                if (channelId) {
                    dispatch(updatePollInChannel({ poll: pollPayload }));
                } else {
                    dispatch(PollReducer.updatePollSliceVotes(pollPayload));
                }
                return { data, vote_id };
            } catch (error) {
                console.error(error);
                dispatch(failedVotingAttempt());
                throw error;
            }
        },
    deletePoll:
        (pollId: string, channelId: string) => async (dispatch: Dispatch) => {
            try {
                await postDeletePoll(pollId);
                dispatch(removePoll({ pollId, channelId }));
            } catch (error) {
                console.error(error);
            }
        },

    closePoll:
        (pollId: string, channelId: string) => async (dispatch: Dispatch) => {
            try {
                await updateClosePoll(pollId);
                dispatch(closePoll({ pollId, channelId }));
            } catch (error) {
                console.error(error);
            }
        },

    deleteComment:
        (channelId: string, pollId: string, commentId: string) =>
        async (dispatch: Dispatch) => {
            try {
                await postdeleteComment(pollId, commentId);
                dispatch(removeComment({ channelId, pollId, commentId }));
            } catch (error) {
                console.error(error);
            }
        },

    fetchSinglePoll: (pollId: string) => async (dispatch: Dispatch) => {
        try {
            const response: any = await getSinglePoll(pollId);

            dispatch(PollReducer.storeSinglePoll(response.data));
        } catch (error) {
            console.error(error);
        }
    },
    toggleLikePoll:
        (pollId: string, channelId: string) => async (dispatch: Dispatch) => {
            try {
                const response: any = await putLikePoll(pollId);
                const updatedLikes = response.data.likes.map(
                    (e: { _id: string; username: string }) => e._id
                );
                dispatch(
                    toggleLikePollInPoll({ pollId, channelId, updatedLikes })
                );
            } catch (error) {
                console.error(error);
            }
        },
    requestPollReport:
        (pollId: string, channelId?: string): AppThunk<Promise<void>> =>
        async (dispatch) => {
            const existing = inFlightReportRequests.get(pollId);
            if (existing) return existing;

            const promise = (async () => {
                try {
                    const report = await ReportService.postPollReport({
                        poll_id: pollId,
                    });
                    // store reports response into poll in redux
                    dispatch(PollReducer.storeReportPoll(report.data));
                    // refresh the channel polls so that the poll in the list contains the new report reference
                    if (channelId) {
                        await dispatch<any>(
                            ChannelActions.fetchChannelPolls(channelId)
                        );
                    }
                } finally {
                    inFlightReportRequests.delete(pollId);
                }
            })();

            inFlightReportRequests.set(pollId, promise);
            return promise;
        },
    exportChannelRawVotes:
        (
            channelId: string,
            format: "csv" | "json" = "csv"
        ): AppThunk<Promise<Blob>> =>
        async () =>
            ReportService.getChannelRawVotes(channelId, format),
    exportPollRawVotes:
        (pollId: string, format: "csv" | "json"): AppThunk<Promise<Blob>> =>
        async () =>
            ReportService.getPollRawVotes(pollId, format),
    exportTextFieldsRaw:
        (channelId: string, format: "csv" | "json"): AppThunk<Promise<Blob>> =>
        async () =>
            ReportService.getTextFieldsRaw(channelId, format),
    fetchPollReport:
        (pollId: string): AppThunk<Promise<void>> =>
        async (dispatch) => {
            try {
                const report = await ReportService.getPollReport(pollId);

                dispatch(PollReducer.storeReportPoll(report));
            } catch (error) {
                console.error(error);
            }
        },
};

export default PolllActions;
